import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  CloudArrowUp, File as FileIcon, FilePdf, FileDoc, FileXls, FileJpg, FileText,
  DownloadSimple, Trash, Link as LinkIcon
} from "@phosphor-icons/react";

const fileIcons = {
  pdf: FilePdf, doc: FileDoc, docx: FileDoc,
  xls: FileXls, xlsx: FileXls,
  jpg: FileJpg, jpeg: FileJpg,
  txt: FileText,
};

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function DocumentPanel({ projectId, user }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { loadDocuments(); }, [projectId]);

  const loadDocuments = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/documents`);
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const allowed = ["jpg", "jpeg", "pdf", "doc", "docx", "xls", "xlsx", "txt"];

    for (const file of files) {
      const ext = file.name.rsplit ? file.name.split(".").pop().toLowerCase() : file.name.split(".").pop()?.toLowerCase();
      if (!allowed.includes(ext)) {
        toast.error(`${file.name}: File type .${ext} not allowed`);
        continue;
      }
      if (file.size > 100 * 1024 * 1024) {
        toast.error(`${file.name}: Exceeds 100MB limit`);
        continue;
      }

      try {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        await api.post(`/projects/${projectId}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`${file.name} uploaded`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || "Error"}`);
      }
    }
    setUploading(false);
    loadDocuments();
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      toast.success("Document deleted");
      loadDocuments();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); handleUpload(Array.from(e.dataTransfer.files)); };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;
  }

  // Group by filename for versioning
  const grouped = {};
  documents.forEach(d => {
    if (!grouped[d.original_filename]) grouped[d.original_filename] = [];
    grouped[d.original_filename].push(d);
  });

  return (
    <div data-testid="document-panel">
      {/* Upload Area */}
      <div
        className={`border-2 border-dashed p-8 mb-6 text-center transition-colors cursor-pointer ${
          dragOver ? "border-[#0055FF] bg-[#F0F7FF]" : "border-[#E5E5E5] hover:border-[#A3A3A3]"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="document-upload-area"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.txt"
          className="hidden"
          onChange={(e) => handleUpload(Array.from(e.target.files))}
          data-testid="document-file-input"
        />
        <CloudArrowUp size={36} className={`mx-auto mb-3 ${dragOver ? "text-[#0055FF]" : "text-[#A3A3A3]"}`} />
        <p className="text-sm font-medium text-[#0A0A0A]">
          {uploading ? "Uploading..." : "Drop files here or click to upload"}
        </p>
        <p className="text-xs text-[#A3A3A3] mt-1">
          JPG, PDF, DOC, XLSX, TXT - Max 100MB
        </p>
      </div>

      {/* Documents List */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
          Documents ({documents.length})
        </h3>
        {Object.entries(grouped).map(([filename, versions]) => {
          const latest = versions[0];
          const ext = filename.split(".").pop()?.toLowerCase() || "";
          const IconComp = fileIcons[ext] || FileIcon;
          return (
            <Card key={filename} className="border border-[#E5E5E5] rounded-none p-4" data-testid={`document-${latest.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F5F5F5] flex items-center justify-center flex-shrink-0">
                  <IconComp size={22} className="text-[#525252]" weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate">{filename}</p>
                    {versions.length > 1 && (
                      <Badge className="bg-[#F5F5F5] text-[#525252] border-0 text-[10px] font-bold px-1.5 py-0 rounded-none">
                        v{latest.version}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#A3A3A3] mt-0.5">
                    <span>{formatSize(latest.size)}</span>
                    <span>by {latest.uploaded_by_name}</span>
                    <span>{new Date(latest.created_at).toLocaleDateString()}</span>
                    {latest.task_id && (
                      <span className="flex items-center gap-1"><LinkIcon size={10} /> Linked</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(latest)}
                    className="h-8 w-8 p-0 hover:bg-[#F5F5F5]"
                    data-testid={`download-${latest.id}`}
                  >
                    <DownloadSimple size={16} className="text-[#525252]" />
                  </Button>
                  {user?.user_type === "internal" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(latest.id)}
                      className="h-8 w-8 p-0 hover:bg-[#FEF2F2]"
                      data-testid={`delete-doc-${latest.id}`}
                    >
                      <Trash size={16} className="text-[#DC2626]" />
                    </Button>
                  )}
                </div>
              </div>
              {/* Version History */}
              {versions.length > 1 && (
                <div className="mt-3 pl-13 space-y-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Version History</p>
                  {versions.slice(1).map(v => (
                    <div key={v.id} className="flex items-center gap-2 text-xs text-[#A3A3A3]">
                      <span>v{v.version}</span>
                      <span>{formatSize(v.size)}</span>
                      <span>{new Date(v.created_at).toLocaleDateString()}</span>
                      <button onClick={() => handleDownload(v)} className="text-[#0055FF] hover:underline">Download</button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
        {documents.length === 0 && (
          <div className="text-center py-12 text-[#A3A3A3]">
            <FileIcon size={36} className="mx-auto mb-2" />
            <p className="font-medium">No documents yet</p>
            <p className="text-xs mt-1">Upload files to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}
